import { get, post } from "src/utils/networkUtils";

/**
 * Lists Gaming Hub notification outbox records. `params` keys match the
 * backend query: channel, status, userOrTeamId, from, to, sort, order, page,
 * limit. Empty values are left out.
 */
export async function listNotificationRecords(params) {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== "" && value != null)
  );
  const body = await get(`/admin/delivery-records/notifications?${query}`);
  return {
    notifications: body?.data?.notifications ?? [],
    total: body?.data?.pagination?.total ?? 0,
    totalIsCapped: body?.data?.pagination?.total_is_capped ?? false,
  };
}

/** One notification record, including the payload sent to Gaming Hub. */
export async function getNotificationRecord(id) {
  const body = await get(
    `/admin/delivery-records/notifications/${encodeURIComponent(id)}`
  );
  return body?.data ?? null;
}

/** Re-queues one notification for sending; the backend refuses in-flight, expired or retired ones. */
export async function resendNotificationRecord(id) {
  await post(
    `/admin/delivery-records/notifications/${encodeURIComponent(id)}/resend`,
    {}
  );
}

/** Re-queues several notifications; returns `{ resent, skipped: [{ id, reason }] }`. */
export async function resendNotificationRecords(ids) {
  const body = await post("/admin/delivery-records/notifications/resend", {
    ids,
  });
  return body?.data ?? { resent: [], skipped: [] };
}

/**
 * Lists gameweek currency records. `params` keys match the backend query:
 * teamId, gameweekId, status, from, to, sort, order, page, limit. Empty
 * values are left out.
 */
export async function listCurrencyRecords(params) {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== "" && value != null)
  );
  const body = await get(`/admin/delivery-records/currencies?${query}`);
  return {
    currencies: body?.data?.currencies ?? [],
    total: body?.data?.pagination?.total ?? 0,
    totalIsCapped: body?.data?.pagination?.total_is_capped ?? false,
  };
}

/** Gameweeks that have currency records, newest first: `[{ id, number, name }]`. */
export async function listCurrencyRecordGameweeks() {
  const body = await get("/admin/delivery-records/currencies/gameweeks");
  return body?.data ?? [];
}

/** One currency record, with the Gaming Hub payload generated from its current amount. */
export async function getCurrencyRecord(id) {
  const body = await get(
    `/admin/delivery-records/currencies/${encodeURIComponent(id)}`
  );
  return body?.data ?? null;
}

/** Finalised gameweeks the currency re-calculator can target, newest first. */
export async function listCurrencyRecalculationGameweeks() {
  const body = await get(
    "/admin/delivery-records/currencies/recalculation/gameweeks"
  );
  return body?.data ?? [];
}

/** `{ gameweek, existing_records, total_teams }` for one gameweek. */
export async function checkCurrencyRecalculation(gameweekId) {
  const query = new URLSearchParams({ gameweekId });
  const body = await get(
    `/admin/delivery-records/currencies/recalculation/check?${query}`
  );
  return body?.data ?? null;
}

/**
 * One read-only re-calculation batch: `{ rows, next_after_team_id }`. Pass
 * `next_after_team_id` back as `afterTeamId` until it comes back null.
 */
export async function previewCurrencyRecalculationBatch(
  gameweekId,
  afterTeamId
) {
  const query = new URLSearchParams({ gameweekId });
  if (afterTeamId) query.set("afterTeamId", afterTeamId);
  const body = await get(
    `/admin/delivery-records/currencies/recalculation/preview?${query}`
  );
  return body?.data ?? { rows: [], next_after_team_id: null };
}

/**
 * Writes one batch of re-calculated currency: `{ scanned, inserted, updated,
 * next_after_team_id }`. Each batch commits on its own, so a retry or resume
 * passes the last returned `next_after_team_id` back as `afterTeamId`.
 */
export async function applyCurrencyRecalculationBatch(gameweekId, afterTeamId) {
  // The re-calculator retries and reports batch failures itself, so a
  // retried or waiting batch must not raise the global error pop-up.
  const body = await post(
    "/admin/delivery-records/currencies/recalculation/apply",
    afterTeamId ? { gameweekId, afterTeamId } : { gameweekId },
    { suppressErrorToast: true }
  );
  return body?.data;
}
